import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MenuItem, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, { RHFSelect, RHFTextField } from 'components/hook-form';
import { IServiceCreate, IViaServiceItem } from '../../../../@types/service';
import RHFJSONEditor from 'components/hook-form/RHFJSONEditor';
import RHFMarkdown from 'components/hook-form/RHFMarkdown';
import { convertStringToArray } from 'utils/func';
import { TypeService } from 'utils/constant';
import ViaService from './ViaService';

// ----------------------------------------------------------------------
type Props = {
  onSubmit: (payload: IServiceCreate) => void;
  currentData: any;
  isUpdate: boolean;
};
type FormValuesProps = {
  name?: string;
  displayname?: string;
  group?: string;
  url?: string;
  regexs?: string;
  type?: string;
  description?: string;
  template?: JSON;
  serviceprovider?: JSON;
  items: any;
  sort: number;
  viaServices: IViaServiceItem[];
};

export default function CreateForm({ onSubmit, currentData, isUpdate }: Props) {
  const BankSchema = Yup.object().shape({
    name: Yup.string().required('Thông tin bắt buộc'),
    displayname: Yup.string().required('Thông tin bắt buộc'),
    type: Yup.string(),
    sort: Yup.number(),
    group: Yup.string(),
    url: Yup.string().required('Thông tin bắt buộc'),
    description: Yup.string().required('Thông tin bắt buộc'),
    viaServices: Yup.array(),
  });

  const defaultValues = {
    name: currentData?.name ?? '',
    displayname: currentData?.displayname ?? '',
    type: currentData?.type ?? '',
    group: currentData?.group ?? '',
    url: currentData?.url ?? '',
    regexs: currentData?.regexs?.join('\n') ?? [],
    description: currentData?.description ?? '',
    template: currentData?.template ?? {},
    serviceprovider: currentData?.serviceprovider ?? {},
    items: currentData?.items ?? [],
    sort: currentData?.sort ?? 0,
    viaServices: currentData?.viaServices ?? [],
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(BankSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmitForm = async (data: FormValuesProps) => {
    const payload: IServiceCreate = {
      name: data?.name ?? '',
      displayname: data?.displayname ?? '',
      group: data?.group ?? '',
      url: data?.url ?? '',
      regexs: convertStringToArray(data?.regexs ?? ''),
      type: data?.type ?? '',
      description: data?.description ?? '',
      template: data?.template ?? ({} as JSON),
      serviceprovider: data?.serviceprovider ?? ({} as JSON),
      items: JSON.stringify(data?.items),
      sort: data.sort,
      viaServices: data.viaServices ?? [],
    };
    onSubmit(payload);
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitForm)}>
      <Stack spacing={3}>
        <RHFTextField name="sort" label="TT Sắp xếp" size="small" />
        <RHFSelect
          name="type"
          label="Kiểu (Seeding/SIM/VIA)"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
        >
          {Object.entries(TypeService).map(([key, value]) => (
            <MenuItem
              key={value}
              value={value}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 0.75,
                typography: 'body2',
                textTransform: 'capitalize',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }}
            >
              {key}
            </MenuItem>
          ))}
        </RHFSelect>
        <RHFTextField name="group" label="Nhóm (Facebook, Youtube...)" size="small" />
        <RHFTextField name="name" label="Tên" size="small" />
        <RHFTextField name="displayname" label="Tên hiển thị" size="small" />
        <RHFTextField name="url" label="url" size="small" />
        {watch('type') === TypeService.OrderSeeding && (
          <RHFTextField name="regexs" label="Regexs" multiline size="small" minRows={3} />
        )}
        {watch('type') === TypeService.OrderSeeding && (
          <RHFJSONEditor name="template" label="Template" />
        )}
        {watch('type') === TypeService.OrderSeeding && (
          <RHFJSONEditor name="serviceprovider" label="Service Provider" />
        )}
        {watch('type') === TypeService.OrderSeeding && (
          <RHFJSONEditor name="items" label="Item Array" />
        )}
        <ViaService
          viaServices={watch('viaServices')}
          onChange={(e) => setValue('viaServices', e)}
        />
        <RHFMarkdown name="description" />
      </Stack>
      <Stack alignItems="flex-end" sx={{ my: 2 }}></Stack>
      <LoadingButton
        fullWidth
        color="inherit"
        size="small"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        {isUpdate ? 'Cập nhật' : 'Tạo mới'}
      </LoadingButton>
    </FormProvider>
  );
}
